import "./compare-styles.scss";

import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { Flyout, FlyoutContent } from "@amzn/stencil-react-components/flyout";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { COMPARE_ANALYTICS_EVENTS, COMPARE_FLYOUT_ACTIONS } from "common/context/compare";
import { getString } from "common/uistringlabels/uiStringUtils";
import { config } from "config";
import { AIForMeTool } from "lib/models/aiforme-models";
import React, { FC, useCallback, useEffect } from "react";

import CompareTable from "./CompareTable";

export interface CompareFlyoutProps {
  /** Whether the flyout is open */
  isOpen: boolean;
  /** Array of tools to compare */
  tools: AIForMeTool[];
  /** Callback to close the flyout */
  onClose: () => void;
  /** Callback to remove a tool from comparison */
  onRemoveTool: (taxonomyId: string) => void;
}

/**
 * CompareFlyout component - Modal flyout displaying comparison table
 * Uses Stencil Flyout component with custom content
 */
export const CompareFlyout: FC<CompareFlyoutProps> = ({ isOpen, tools, onClose, onRemoveTool }) => {
  /**
   * Track analytics event for flyout actions
   */
  const trackFlyoutAction = useCallback(
    (action: string, additionalData?: Record<string, any>) => {
      const trackerId = config.analytics.uxmAppId;
      const event = {
        action,
        toolCount: tools.length,
        ...additionalData,
      };
      trackCustomEvent(event, COMPARE_ANALYTICS_EVENTS.FLYOUT, trackerId);
    },
    [tools.length]
  );

  /**
   * Track flyout open event
   */
  useEffect(() => {
    if (isOpen) {
      trackFlyoutAction(COMPARE_FLYOUT_ACTIONS.OPEN, {
        toolIds: tools.map((t) => t.taxonomyId),
      });
    }
  }, [isOpen, tools, trackFlyoutAction]);

  /**
   * Handle flyout close
   */
  const handleClose = useCallback(() => {
    trackFlyoutAction(COMPARE_FLYOUT_ACTIONS.CLOSE);
    onClose();
  }, [onClose, trackFlyoutAction]);

  /**
   * Handle removing a tool from the table
   */
  const handleRemoveTool = useCallback(
    (taxonomyId: string) => {
      const tool = tools.find((t) => t.taxonomyId === taxonomyId);
      trackFlyoutAction(COMPARE_FLYOUT_ACTIONS.REMOVE_TOOL, {
        taxonomyId,
        title: tool?.title,
      });
      onRemoveTool(taxonomyId);
    },
    [onRemoveTool, tools, trackFlyoutAction]
  );

  const closeButton = (
    <Button
      variant={ButtonVariant.Primary}
      onClick={handleClose}
      data-testid="compare-flyout-close"
      touchpoint="ai-for-me-compare-flyout-close"
    >
      {getString("aiForMe.compare.close")}
    </Button>
  );

  return (
    <Flyout isOpen={isOpen} close={handleClose} dataTestId="compare-flyout">
      <FlyoutContent
        titleText={getString("aiForMe.compare.flyoutTitle")}
        onCloseButtonClick={handleClose}
        buttons={[closeButton]}
        minWidth={"70vw"}
      >
        <div className="compare-flyout__content">
          <CompareTable tools={tools} onRemoveTool={handleRemoveTool} />
        </div>
      </FlyoutContent>
    </Flyout>
  );
};

export default CompareFlyout;
